/**
 * AURA Dashboard — Soho + Artists & Robots Intelligence Control Surface
 *
 * Overlays on top of the 3D field when toggled (press D).
 * Shows: cluster health, recent signals, agent status, ingestion activity.
 * Built as a plain DOM overlay — no framework, no build deps.
 */

const GATEWAY = import.meta.env.VITE_API_URL || '';

function fmt(n: number) {
  return n?.toFixed ? n.toFixed(2) : n;
}

function timeAgo(ts: number) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const CSS = `
  #aura-dashboard {
    position: fixed; top: 0; right: 0; width: 380px; height: 100vh;
    background: rgba(6,6,13,0.96); border-left: 1px solid rgba(255,255,255,0.08);
    color: #e2e8f0; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; font-size: 11px;
    overflow-y: auto; z-index: 1000; padding: 16px;
    backdrop-filter: blur(20px);
    transform: translateX(100%); transition: transform 0.3s ease;
  }
  #aura-dashboard.open { transform: translateX(0); }
  .dash-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
  .dash-title { font-size: 11px; font-weight: 400; letter-spacing: 0.01em; color: rgba(255,255,255,0.45); }
  .dash-subtitle { font-size: 9px; color: #475569; margin-top: 2px; letter-spacing: 0.04em; }
  .dash-section { margin-bottom: 20px; }
  .dash-section-title { font-size: 8px; letter-spacing: 0.14em; font-weight: 700; color: rgba(255,255,255,0.28); text-transform: uppercase; margin-bottom: 8px; }
  .dash-cluster {
    padding: 8px 10px; margin-bottom: 4px; border-radius: 4px;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06);
    cursor: default;
  }
  .dash-cluster:hover { background: rgba(255,255,255,0.06); }
  .dash-cluster-name { font-weight: 600; font-size: 11px; color: #e2e8f0; margin-bottom: 3px; }
  .dash-cluster-meta { display: flex; gap: 12px; font-size: 10px; color: #64748b; }
  .dash-cluster-agent { font-size: 10px; color: #22c55e; margin-top: 2px; }
  .dash-cluster-no-agent { font-size: 10px; color: #f59e0b; margin-top: 2px; }
  .trend-rising { color: #4fd1a0; }  /* teal = momentum */
  .trend-stable { color: #64748b; }
  .trend-fading { color: #ef4444; }
  .dash-signal { padding: 6px 10px; margin-bottom: 3px; border-radius: 3px; background: rgba(255,255,255,0.02); }
  .dash-signal-content { font-size: 10px; color: #94a3b8; line-height: 1.4; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .dash-signal-meta { font-size: 9px; color: #475569; margin-top: 2px; }
  .dash-stat { display: inline-block; background: rgba(255,255,255,0.05); border-radius: 3px; padding: 4px 8px; margin-right: 6px; margin-bottom: 6px; }
  .dash-stat-val { font-size: 18px; font-weight: 700; color: #fff; }
  .dash-stat-label { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 0.1em; }
  .dash-btn {
    background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
    color: #e2e8f0; padding: 5px 10px; border-radius: 3px; font-family: inherit;
    font-size: 10px; cursor: pointer; letter-spacing: 0.05em;
  }
  .dash-btn:hover { background: rgba(255,255,255,0.12); }
  .dash-close { background: none; border: none; color: #64748b; cursor: pointer; font-size: 16px; }
  .dash-loading { color: #475569; font-size: 10px; padding: 8px; }
  .dash-ingestion-status { padding: 8px 10px; background: rgba(34,197,94,0.08); border: 1px solid rgba(34,197,94,0.2); border-radius: 4px; font-size: 10px; color: #22c55e; }
  /* Toggle button removed — accessible via keyboard D shortcut only */
`;

export class Dashboard {
  private el: HTMLElement;
  private toggle!: HTMLElement; // not rendered — keyboard shortcut D only
  private open = false;
  private refreshTimer: any = null;

  constructor() {
    // Inject styles
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    // No visible toggle — keyboard shortcut D only

    // Dashboard panel
    this.el = document.createElement('div');
    this.el.id = 'aura-dashboard';
    this.el.innerHTML = `
      <div class="dash-header">
        <div>
          <div class="dash-title">Soho + Artists & Robots</div>
          <div class="dash-subtitle">Live intelligence graph</div>
        </div>
        <button class="dash-close" id="dash-close-btn">×</button>
      </div>
      <div id="dash-stats"></div>
      <div class="dash-section">
        <div class="dash-section-title">Intelligence Clusters</div>
        <div id="dash-clusters"><div class="dash-loading">Loading…</div></div>
      </div>
      <div class="dash-section">
        <div class="dash-section-title">Recent Signals</div>
        <div id="dash-signals"><div class="dash-loading">Loading…</div></div>
      </div>
      <div class="dash-section">
        <div class="dash-section-title">System</div>
        <div id="dash-system"><div class="dash-loading">Loading…</div></div>
      </div>
    `;
    document.body.appendChild(this.el);

    document.getElementById('dash-close-btn')!.onclick = () => this.setOpen(false);

    // Keyboard shortcut: D
    document.addEventListener('keydown', (e) => {
      if (e.key === 'd' || e.key === 'D') {
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
        this.setOpen(!this.open);
      }
    });
  }

  setOpen(open: boolean) {
    this.open = open;
    this.el.classList.toggle('open', open);
    if (open) {
      this.refresh();
      this.refreshTimer = setInterval(() => this.refresh(), 30_000);
    } else {
      clearInterval(this.refreshTimer);
    }
  }

  private async refresh() {
    await Promise.all([
      this.loadClusters(),
      this.loadSignals(),
      this.loadSystem(),
    ]);
  }

  private async loadClusters() {
    try {
      const r = await fetch(`${GATEWAY}/api/clusters?limit=50`);
      const { clusters, total } = await r.json() as any;

      // Stats row
      const active = clusters.filter((c: any) => c.status === 'ACTIVE').length;
      const withAgent = clusters.filter((c: any) => c.metadata?.agentStudioId).length;
      document.getElementById('dash-stats')!.innerHTML = `
        <div style="margin-bottom:16px">
          <div class="dash-stat"><div class="dash-stat-val">${total}</div><div class="dash-stat-label">Clusters</div></div>
          <div class="dash-stat"><div class="dash-stat-val">${withAgent}</div><div class="dash-stat-label">Agents</div></div>
        </div>
      `;

      const el = document.getElementById('dash-clusters')!;
      el.innerHTML = clusters.slice(0, 20).map((c: any) => {
        const conf = (c.confidence * 100).toFixed(0);
        const signals = (c.signalIds || []).length;
        const trendClass = signals >= 8 ? 'trend-rising' : signals >= 3 ? 'trend-stable' : 'trend-fading';
        const trend = signals >= 8 ? '↑' : signals >= 3 ? '→' : '↓';
        const agentLine = c.metadata?.agentStudioId
          ? `<div class="dash-cluster-agent">● Agent Studio agent active</div>`
          : `<div class="dash-cluster-no-agent">○ No agent provisioned</div>`;
        return `
          <div class="dash-cluster">
            <div class="dash-cluster-name">${c.topic}</div>
            <div class="dash-cluster-meta">
              <span class="${trendClass}">${trend}</span>
              <span>${conf}% confidence</span>
              <span>${signals} signals</span>
              <span>${timeAgo(c.updated_at)}</span>
            </div>
            ${agentLine}
          </div>
        `;
      }).join('');
    } catch {
      document.getElementById('dash-clusters')!.innerHTML = '<div class="dash-loading">Failed to load</div>';
    }
  }

  private async loadSignals() {
    try {
      const r = await fetch(`${GATEWAY}/api/v1/signals?limit=10`);
      const { signals } = await r.json() as any;
      const el = document.getElementById('dash-signals')!;
      el.innerHTML = (signals || []).map((s: any) => `
        <div class="dash-signal">
          <div class="dash-signal-content">${s.content?.slice(0, 80) || '—'}</div>
          <div class="dash-signal-meta">${s.source} · ${(s.confidence * 100).toFixed(0)}% · ${timeAgo(s.created_at)}</div>
        </div>
      `).join('') || '<div class="dash-loading">No signals yet</div>';
    } catch {
      document.getElementById('dash-signals')!.innerHTML = '<div class="dash-loading">Failed to load</div>';
    }
  }

  private async loadSystem() {
    try {
      const [healthR, ingestR] = await Promise.all([
        fetch(`${GATEWAY}/health`),
        fetch(`${GATEWAY}/api/ingestion/status`),
      ]);
      const health = await healthR.json() as any;
      const ingest = await ingestR.json() as any;

      const el = document.getElementById('dash-system')!;
      const svcLines = Object.entries(health.services || {})
        .map(([k, v]) => `<span class="${v === 'up' ? 'trend-rising' : 'trend-fading'}">● ${k}</span>`)
        .join('  ');

      el.innerHTML = `
        <div style="margin-bottom:8px;font-size:10px">${svcLines}</div>
        <div class="${ingest.isPolling ? 'dash-ingestion-status' : 'dash-signal'}">
          ${ingest.isPolling ? '● Auto-ingestion running' : '○ Ingestion idle'}
          · ${ingest.totalProcessed || 0} signals processed
        </div>
        <div style="margin-top:10px;display:flex;gap:6px;flex-wrap:wrap">
          <button class="dash-btn" onclick="fetch('${GATEWAY}/api/ingestion/cluster',{method:'POST'}).then(()=>this.textContent='Done!')">Run Cluster</button>
          <button class="dash-btn" onclick="fetch('${GATEWAY}/api/ai/semantic-cluster',{method:'POST'}).then(()=>this.textContent='Done!')">Semantic Cluster</button>
          <button class="dash-btn" onclick="fetch('${GATEWAY}/api/ai/query',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({query:'provision_cluster_agents'})}).then(()=>this.textContent='Done!')">Provision Agents</button>
        </div>
      `;
    } catch {
      document.getElementById('dash-system')!.innerHTML = '<div class="dash-loading">Failed to load</div>';
    }
  }
}
